import { Flex } from 'antd'
import {
  FunctionComponent,
  ReactNode,
  useCallback,
  useMemo,
  useState,
} from 'react'
import useIntl from 'react-intl/src/components/useIntl'
import styled, { useTheme } from 'styled-components'
import GradientSkeleton from '@/components/common/gradient-skeleton'
import PostImage from '@/components/common/image/post-image'
import { NoData } from '@/components/kol/detail/no-data'
import useKolPosts from '@/components/kol/hooks/use-kol-posts'
import SocialMediaPostModal, {
  SocialMediaPost,
} from '@/components/modal/social-media-post-modal'
import { Post } from '@/types/redux/kol-content'
import { KolSchemaModel } from '@/types/schema/kol-schema'
import { type VisitKolSocialMediaProperties } from '@/utils/ampli'
import {
  convertPlatformType,
  PlatformShortcode,
} from '@/utils/convert-platform'
import getPostThumbnailUrls from '@/utils/kol/get-post-thumbnail-urls'

interface PostListProps {
  kolInfo?: KolSchemaModel
  isKolFetching?: boolean
  platformShortCode?: PlatformShortcode
  from: VisitKolSocialMediaProperties['from']
}
const PostList: FunctionComponent<PostListProps> = ({
  kolInfo,
  platformShortCode,
  isKolFetching,
  from,
}) => {
  const { formatMessage } = useIntl()
  const theme = useTheme()
  const [selectedPost, setSelectedPost] = useState<SocialMediaPost>()
  const { response, isFetching: isPostsFetching } = useKolPosts({
    kolId: kolInfo?.id,
    platform: platformShortCode,
    request: { size: 4 },
  })

  const handlePostClick = useCallback(
    (post: Post): void => {
      const postPlatform = convertPlatformType(post.platformType)
      if (!postPlatform || !kolInfo) {
        return
      }

      setSelectedPost({
        crawledID: post.crawledID,
        platformCode: postPlatform.code,
        platformTitle: postPlatform.title,
        postID: post.postID,
        postType: post.postType,
        url: post.url,
        kolId: kolInfo.id,
        kolDcIdHashed: kolInfo.dcIdHashed,
        kolName: kolInfo.name,
      })
    },
    [kolInfo],
  )

  const PostImages = useMemo(() => {
    if (!response?.data) {
      return []
    }

    return response.data.map((post) => (
      <div
        key={post.postID}
        role='button'
        onClick={() => handlePostClick(post)}
      >
        {kolInfo?.id && (
          <PostImage
            height={126}
            key={post.postID}
            kolUuid={kolInfo.id}
            thumbnailUrls={getPostThumbnailUrls(post)}
            width={126}
          />
        )}
      </div>
    ))
  }, [handlePostClick, kolInfo?.id, response?.data])

  return (
    <Wrapper>
      <SectionTitle>
        {formatMessage({
          id: 'general:latest_post',
        })}
      </SectionTitle>
      <Flex gap={16}>
        {((): ReactNode => {
          if (!!isKolFetching || isPostsFetching) {
            return Array.from({ length: 4 }).map((_, index) => (
              <GradientSkeleton
                active
                endColor={theme.colors.base.grey.grey6}
                fromColor={theme.colors.base.grey.grey6}
                height='126px'
                key={index}
                style={{
                  borderRadius: '3px',
                }}
                type='squareButton'
                width='126px'
              />
            ))
          }

          if (!response?.data?.length) {
            return (
              <NodataBox>
                <NoData />
              </NodataBox>
            )
          }

          return PostImages
        })()}
      </Flex>

      {selectedPost && (
        <SocialMediaPostModal
          isClickedFromDrawer
          from={from}
          post={selectedPost}
          onCancel={(): void => setSelectedPost(undefined)}
        />
      )}
    </Wrapper>
  )
}

export default PostList

const SectionTitle = styled.div`
  font-size: 16px;
  font-weight: 700;
  line-height: 140%;
  color: ${({ theme }): string => theme.colors.text.secondary};
`

const NodataBox = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  width: 100%;

  background: ${({ theme }): string => theme.colors.background.white};
  border-radius: 4px;
  box-shadow: 0 2px 12px 0 rgb(0, 0, 0, 0.1);
`

const Wrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 8px;
`
